import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import Papa from "papaparse";

/**
 * Exports PrimeReact DataTable data to a PDF file.
 * @param {string} fileName - The base file name for the export.
 * @param {string} billNumber - The bill number to include in the filename.
 * @param {Array} columns - Column definitions with field and header properties.
 * @param {Array} data - The data to export.
 */
export const exportToPDF = async (fileName, billNumber, columns, data, options) => {
  if (!fileName || !billNumber || !columns || !data) {
    console.error("Missing required parameters for exportToPDF");
    return;
  }

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const today = new Date()
    .toLocaleDateString("en-US", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
    })
    .replace(/\//g, "-");
  const fileNameWithDate = `${billNumber}-${fileName}-${today}`;

  const margin = 36;
  const pageWidth = doc.internal.pageSize.width;
  const logoPath = "/logo192.png"; // Ensure this path is correct

  // Convert image to Base64
  const convertToBase64 = (path) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous"; // Prevent CORS issues
      img.src = path;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => {
        console.error("Failed to load image:", path);
        resolve(null); // Resolve with null if image fails
      };
    });
  };

  try {
    const logoBase64 = await convertToBase64(logoPath);
    if (logoBase64) {
      doc.addImage(logoBase64, "PNG", margin, 20, 40, 40);
    }

    doc.setFontSize(10);
    doc.text("69th Legislature 2025 Session", pageWidth / 2, margin, {
      align: "center",
    });

    let yPos = margin + 20;
    doc.setFontSize(16);
    doc.text(fileNameWithDate, pageWidth / 2, yPos, { align: "center" });
    yPos += 10;

    const headers = columns.map((col) => col.header);
    const body = data.map((row) => columns.map((col) => row[col.field] || ""));

    autoTable(doc, {
      startY: yPos + 10,
      head: [headers],
      body: body,
      margin: { left: margin, right: margin },
      headStyles: { fillColor: [103, 137, 127] },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      styles: { fontSize: 8 },
      columnStyles: options.columnStyles || {},
      didParseCell: (hookData) => {
        if (hookData.section === "head" && options.centerHeaders?.includes(hookData.cell.raw)) {
          hookData.cell.styles.halign = "center"; // Center-align headers
        }
      },
    });

    doc.save(`${fileNameWithDate}.pdf`);
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};

/**
 * Exports PrimeReact DataTable data to a CSV file.
 * @param {string} fileName - The base file name for the export.
 * @param {string} billNumber - The bill number to include in the filename.
 * @param {Array} columns - Column definitions with field and header properties.
 * @param {Array} data - The data to export.
 */
export const exportToCSV = (fileName, billNumber, columns, data) => {
  if (!fileName || !billNumber || !columns || !data || data.length === 0) {
    console.error("Missing or empty data for exportToCSV");
    return;
  }

  const today = new Date()
    .toLocaleDateString("en-US", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
    })
    .replace(/\//g, "-");
  const fileNameWithDate = `${billNumber}-${fileName}-${today}`;

  const headers = columns.map((col) => col.header);
  const csvData = data.map((row) => {
    return columns.map((col) =>
      row[col.field] !== undefined ? row[col.field] : ""
    );
  });

  try {
    const csv = Papa.unparse({
      fields: headers,
      data: csvData,
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `${fileNameWithDate}.csv`);
  } catch (error) {
    console.error("Error generating CSV:", error);
  }
};
